import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session timeout in milliseconds (15 minutes of inactivity)
const SESSION_TIMEOUT = 15 * 60 * 1000;
// Warning before timeout (2 minutes before)
const WARNING_TIME = 2 * 60 * 1000;
// Activity ping interval (every 5 minutes)
const ACTIVITY_PING_INTERVAL = 5 * 60 * 1000;

const useSessionTimeout = (onLogout) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const activityRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (activityRef.current) clearInterval(activityRef.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearTimers();
    toast.error('Session expired due to inactivity. Please log in again.');
    if (onLogout) onLogout();
  }, [clearTimers, onLogout]);

  const showWarning = useCallback(() => {
    toast.warning('Your session will expire in 2 minutes due to inactivity. Move your mouse or click to stay logged in.', {
      duration: 10000,
    });
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    lastActivityRef.current = Date.now();

    // Set warning timer
    warningRef.current = setTimeout(() => {
      showWarning();
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, SESSION_TIMEOUT);
  }, [clearTimers, handleLogout, showWarning]);

  const pingActivity = useCallback(async () => {
    try {
      await fetch(`${API}/profile/activity`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Silent fail - activity ping is optional, network errors expected
    }
  }, []);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    // Only reset if significant time has passed since last activity
    if (now - lastActivityRef.current > 1000) {
      resetTimers();
    }
  }, [resetTimers]);

  useEffect(() => {
    // Start timers
    resetTimers();

    // Set up activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Set up activity ping
    activityRef.current = setInterval(pingActivity, ACTIVITY_PING_INTERVAL);

    // Cleanup
    return () => {
      clearTimers();
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [resetTimers, handleUserActivity, clearTimers, pingActivity]);

  return { resetTimers };
};

export default useSessionTimeout;
